import React, { useMemo, useState } from "react";
import { Badge, Button, Form, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faSyncAlt } from "@fortawesome/free-solid-svg-icons";
import HelpTip from "app/orderTool/components/HelpTip";
import { normalizeDateYmd, resolveSlotEntryOn } from "helper/packageRotationHelper";
import { frDate, todayYmd } from "./slotLabels";

const dayBefore = (ymd) => {
  const d = new Date(`${ymd}T12:00:00`);
  d.setDate(d.getDate() - 1);
  return normalizeDateYmd(d);
};

/**
 * Date cible par défaut : la prochaine « date de carte », c.-à-d. la date
 * future qui concentre le plus d'entrées de calendrier (ex : 12/10).
 */
const defaultTargetDate = (slots) => {
  const today = todayYmd();
  const counts = {};
  slots.forEach((slot) => {
    (slot.entries || []).forEach((e) => {
      const from = normalizeDateYmd(e.effective_from);
      if (from && from > today) counts[from] = (counts[from] || 0) + 1;
    });
  });
  const dates = Object.keys(counts).sort(
    (a, b) => counts[b] - counts[a] || a.localeCompare(b)
  );
  return dates[0] || today;
};

/**
 * Préparation d'un changement de carte : pour une date cible, l'état de
 * chaque slot — ce qui bascule, ce qui reste, ce qui n'est pas résolu
 * (produit à créer au BO), les slots sans forfait rattaché, et l'accès
 * direct à la cascade des devis déjà signés.
 */
const PreparationCarte = ({ slots, onSelectSlot, onCascade }) => {
  const [date, setDate] = useState(() => defaultTargetDate(slots));

  const rows = useMemo(() => {
    const d = normalizeDateYmd(date) || todayYmd();
    const before = dayBefore(d);
    return [...slots]
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
      .map((slot) => {
        const entryBefore = resolveSlotEntryOn(slot, before);
        const entryAt = resolveSlotEntryOn(slot, d);
        const entryExactlyAt = (slot.entries || []).find(
          (e) => normalizeDateYmd(e.effective_from) === d
        );
        const nbPackages = (slot.package_lines || []).length;
        let status;
        if (!entryAt) status = "unresolved";
        else if (entryExactlyAt && entryBefore && entryBefore.item_id !== entryAt.item_id) status = "switch";
        else if (entryExactlyAt && !entryBefore) status = "start";
        else status = "unchanged";
        return { slot, entryBefore, entryAt, entryExactlyAt, nbPackages, status };
      });
  }, [slots, date]);

  const counts = useMemo(
    () => ({
      switch: rows.filter((r) => r.status === "switch").length,
      unresolved: rows.filter((r) => r.status === "unresolved").length,
      unchanged: rows.filter((r) => r.status === "unchanged" || r.status === "start").length,
      noPackage: rows.filter((r) => r.nbPackages === 0).length,
    }),
    [rows]
  );

  const statusBadge = (row) => {
    switch (row.status) {
      case "switch":
        return <Badge variant="primary">Bascule</Badge>;
      case "unresolved":
        return <Badge variant="danger">Non résolu</Badge>;
      case "start":
        return <Badge variant="info">Démarre</Badge>;
      default:
        return <Badge variant="secondary">Inchangé</Badge>;
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center flex-wrap mb-3">
        <Form.Label className="mb-0 mr-2 font-weight-bold">Date de la nouvelle carte :</Form.Label>
        <Form.Control
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          style={{ maxWidth: 180 }}
        />
        <HelpTip title="Préparer un changement de carte" placement="right">
          <p className="mb-1">
            Pour la date choisie (pré-remplie avec la prochaine date qui concentre le plus de
            bascules) : ce que chaque slot <strong>devient</strong>, ce qui reste inchangé, et
            surtout ce qui <strong>bloque</strong> :
          </p>
          <p className="mb-1">
            <Badge variant="danger">Non résolu</Badge> = pas d'entrée valable à cette date — le
            produit est probablement à créer au BO, puis à saisir dans le calendrier (✏️).
            <br />
            <Badge variant="warning">0 forfait</Badge> = le slot ne sert encore à rien.
          </p>
          <p className="mb-0">
            Le bouton 🔄 ouvre l'aperçu des devis déjà signés à synchroniser pour la bascule
            (à faire le jour J ou après).
          </p>
        </HelpTip>
        <span className="ml-auto">
          <Badge variant="primary" className="mr-1">{counts.switch} bascule(s)</Badge>
          <Badge variant="danger" className="mr-1">{counts.unresolved} non résolu(s)</Badge>
          <Badge variant="secondary" className="mr-1">{counts.unchanged} inchangé(s)</Badge>
          <Badge variant="warning">{counts.noPackage} sans forfait</Badge>
        </span>
      </div>

      <Table striped hover size="sm">
        <thead>
          <tr>
            <th>Slot</th>
            <th>La veille ({frDate(dayBefore(normalizeDateYmd(date) || todayYmd()))})</th>
            <th>À partir du {frDate(date)}</th>
            <th>Statut</th>
            <th>Forfaits</th>
            <th style={{ width: 110 }}></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ slot, entryBefore, entryAt, entryExactlyAt, nbPackages, status }) => (
            <tr key={slot.id} className={status === "unresolved" ? "table-danger" : undefined}>
              <td>
                <span
                  role="button"
                  className="text-primary"
                  style={{ cursor: "pointer" }}
                  onClick={() => onSelectSlot && onSelectSlot(slot.id)}
                  title="Ouvrir la gestion de ce slot"
                >
                  {slot.name}
                </span>
              </td>
              <td>{entryBefore?.name || <span className="text-muted">—</span>}</td>
              <td className={status === "switch" ? "font-weight-bold text-primary" : undefined}>
                {entryAt?.name || <span className="text-danger">à compléter</span>}
              </td>
              <td>{statusBadge({ status })}</td>
              <td>
                {nbPackages > 0 ? (
                  nbPackages
                ) : (
                  <Badge variant="warning">0 forfait</Badge>
                )}
              </td>
              <td className="text-right text-nowrap">
                {entryExactlyAt && nbPackages > 0 && (
                  <Button
                    size="sm"
                    variant="outline-primary"
                    className="mr-1"
                    title="Devis déjà signés à synchroniser pour cette bascule"
                    onClick={() => onCascade && onCascade(entryExactlyAt, slot)}
                  >
                    <FontAwesomeIcon icon={faSyncAlt} />
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline-secondary"
                  title="Compléter le calendrier de ce slot"
                  onClick={() => onSelectSlot && onSelectSlot(slot.id)}
                >
                  <FontAwesomeIcon icon={faPen} />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default PreparationCarte;
